import { createStorefrontApiClient } from '@shopify/storefront-api-client';

// Check if Shopify is properly configured
const isShopifyConfigured = process.env.REACT_APP_SHOPIFY_DOMAIN && 
  process.env.REACT_APP_SHOPIFY_STOREFRONT_ACCESS_TOKEN &&
  process.env.REACT_APP_SHOPIFY_DOMAIN !== 'your-shop.myshopify.com' &&
  process.env.REACT_APP_SHOPIFY_STOREFRONT_ACCESS_TOKEN !== 'your-storefront-token';

if (!isShopifyConfigured) {
  console.warn('Shopify not configured or using placeholder values. Shopify features will be disabled.');
}

// Shopify Admin API configuration for custom pricing
const ADMIN_API_CONFIG = {
  storeDomain: process.env.REACT_APP_SHOPIFY_STORE_DOMAIN,
  apiVersion: process.env.REACT_APP_SHOPIFY_API_VERSION,
  accessToken: process.env.REACT_APP_SHOPIFY_ADMIN_ACCESS_TOKEN,
};

// Create Admin API client for draft orders (custom pricing)
const adminClient = {
  request: async (query, variables = {}) => {
    if (!isShopifyConfigured) {
      throw new Error('Shopify not configured');
    }

    const url = `https://${ADMIN_API_CONFIG.storeDomain}/admin/api/${ADMIN_API_CONFIG.apiVersion}/graphql.json`;
    
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Access-Token': ADMIN_API_CONFIG.accessToken,
      },
      body: JSON.stringify({
        query,
        variables,
      }),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    
    if (data.errors) {
      throw new Error(`GraphQL errors: ${JSON.stringify(data.errors)}`);
    }

    return data;
  }
};

// Create Storefront API client conditionally
const client = isShopifyConfigured ? createStorefrontApiClient({
  storeDomain: process.env.REACT_APP_SHOPIFY_DOMAIN,
  apiVersion: '2023-10',
  publicAccessToken: process.env.REACT_APP_SHOPIFY_STOREFRONT_ACCESS_TOKEN,
}) : null;

// GraphQL mutations for cart operations (updated from deprecated checkout API)
const CREATE_CART_MUTATION = `
  mutation cartCreate($input: CartInput!) {
    cartCreate(input: $input) {
      cart {
        id
        checkoutUrl
        lines(first: 10) {
          edges {
            node {
              id
              quantity
              merchandise {
                ... on ProductVariant {
                  id
                  title
                  product {
                    title
                  }
                }
              }
              cost {
                totalAmount {
                  amount
                  currencyCode
                }
              }
            }
          }
        }
        cost {
          totalAmount {
            amount
            currencyCode
          }
          subtotalAmount {
            amount
            currencyCode
          }
        }
      }
      userErrors {
        field
        message
        code
      }
    }
  }
`;

const UPDATE_CART_BUYER_IDENTITY_MUTATION = `
  mutation cartBuyerIdentityUpdate($cartId: ID!, $buyerIdentity: CartBuyerIdentityInput!) {
    cartBuyerIdentityUpdate(cartId: $cartId, buyerIdentity: $buyerIdentity) {
      cart {
        id
        buyerIdentity {
          email
          phone
          countryCode
        }
      }
      userErrors {
        field
        message
        code
      }
    }
  }
`;

const CART_QUERY = `
  query cart($id: ID!) {
    cart(id: $id) {
      id
      checkoutUrl
      lines(first: 10) {
        edges {
          node {
            id
            quantity
            merchandise {
              ... on ProductVariant {
                id
                title
                product {
                  title
                }
              }
            }
            cost {
              totalAmount {
                amount
                currencyCode
              }
            }
          }
        }
      }
      cost {
        totalAmount {
          amount
          currencyCode
        }
        subtotalAmount {
          amount
          currencyCode
        }
      }
      buyerIdentity {
        email
        phone
        countryCode
      }
    }
  }
`;

// Test connection query
const TEST_CONNECTION_QUERY = `
  query {
    products(first: 1) {
      edges {
        node {
          id
          title
        }
      }
    }
  }
`;

// Query to get products with variants for testing
const GET_PRODUCTS_QUERY = `
  query getProducts($first: Int!) {
    products(first: $first) {
      edges {
        node {
          id
          title
          handle
          variants(first: 5) {
            edges {
              node {
                id
                title
                price {
                  amount
                  currencyCode
                }
              }
            }
          }
        }
      }
    }
  }
`;

// Draft Order mutation for custom pricing
const CREATE_DRAFT_ORDER_MUTATION = `
  mutation draftOrderCreate($input: DraftOrderInput!) {
    draftOrderCreate(input: $input) {
      draftOrder {
        id
        name
        invoiceUrl
        totalPrice
        currencyCode
        status
        order {
          id
          name
        }
      }
      userErrors {
        field
        message
      }
    }
  }
`;

// Complete draft order mutation
const DRAFT_ORDER_COMPLETE_MUTATION = `
  mutation draftOrderComplete($id: ID!) {
    draftOrderComplete(id: $id) {
      draftOrder {
        id
        order {
          id
          name
          totalPrice
          checkoutUrl
        }
      }
      userErrors {
        field
        message
      }
    }
  }
`;

// GraphQL mutation to apply discount codes to cart
const CART_DISCOUNT_CODES_UPDATE_MUTATION = `
  mutation cartDiscountCodesUpdate($cartId: ID!, $discountCodes: [String!]) {
    cartDiscountCodesUpdate(cartId: $cartId, discountCodes: $discountCodes) {
      cart {
        id
        discountCodes {
          code
          applicable
        }
        cost {
          totalAmount {
            amount
            currencyCode
          }
          subtotalAmount {
            amount
            currencyCode
          }
        }
      }
      userErrors {
        field
        message
        code
      }
    }
  }
`;

/**
 * Test Shopify API connection
 */
export const testConnection = async () => {
  if (!client) {
    console.warn('Shopify not configured - skipping connection test');
    return { success: false, error: 'Shopify not configured' };
  }

  try {
    console.log('Testing Shopify connection...');
    
    const response = await client.request(TEST_CONNECTION_QUERY);
    
    console.log('Shopify API Response:', response);
    
    if (response.data && response.data.products) {
      console.log('✅ Shopify connection successful!');
      return { success: true, data: response.data };
    } else {
      console.error('❌ Unexpected response structure:', response);
      return { success: false, error: 'Unexpected response structure' };
    }
  } catch (error) {
    console.error('❌ Shopify connection failed:', error);
    
    // Extract GraphQL errors if available
    let errorMessage = error.message || 'Unknown error';
    if (error.graphQLErrors && error.graphQLErrors.length > 0) {
      errorMessage = error.graphQLErrors.map(err => err.message).join(', ');
    }
    
    return { success: false, error: errorMessage };
  }
};

/**
 * Get products from store for testing
 */
export const getProducts = async (limit = 5) => {
  if (!client) {
    console.warn('Shopify not configured - skipping product fetch');
    return { success: false, error: 'Shopify not configured' };
  }

  try {
    console.log('Fetching products from store...');
    
    const variables = { first: limit };
    const response = await client.request(GET_PRODUCTS_QUERY, { variables });
    
    console.log('Products response:', response);
    
    if (response.data && response.data.products) {
      return { success: true, products: response.data.products.edges };
    } else {
      return { success: false, error: 'No products found' };
    }
  } catch (error) {
    console.error('❌ Error fetching products:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Convert cart items to Shopify format with fallback for missing variant IDs
 */
export const convertCartItemsToShopify = (cartItems) => {
  console.log('Converting cart items to Shopify format:', cartItems);
  
  return cartItems.map((item, index) => {
    console.log(`Processing item ${index}:`, item);
    
    // Check if we have a proper variantId (Shopify GID format)
    let variantId = item.variantId;
    
    if (!variantId) {
      // For testing: create a placeholder variant ID
      // In production, you'd need real variant IDs from your Shopify store
      console.warn(`Item ${index} missing variantId, using placeholder for testing`);
      variantId = 'gid://shopify/ProductVariant/12345678901234567890'; // Placeholder
    }
    
    // Ensure variantId is in proper Shopify GID format
    if (!variantId.startsWith('gid://shopify/ProductVariant/')) {
      variantId = `gid://shopify/ProductVariant/${variantId}`;
    }
    
    if (!item.quantity || item.quantity <= 0) {
      throw new Error(`Item ${index} has invalid quantity: ${item.quantity}`);
    }
    
    // Convert to Shopify format with safe string conversion
    const shopifyItem = {
      merchandiseId: variantId,
      quantity: parseInt(item.quantity),
      attributes: [
        { key: 'productId', value: (item.productId || item.id || '').toString() },
        { key: 'handle', value: item.handle || '' },
        { key: 'title', value: item.title || '' },
        { key: 'originalPrice', value: (item.price || '').toString() }, // Store original price for reference
        { key: 'orderNumber', value: item.title || '' } // Store order info
      ]
    };

    // Note: Shopify Cart API uses the variant's catalog price, not custom prices
    // The 'price' field in item is stored as an attribute for reference only
    
    console.log(`Converted item ${index}:`, shopifyItem);
    return shopifyItem;
  });
};

/**
 * Create a new cart with items
 */
export const createCart = async (cartItems) => {
  if (!client) {
    console.warn('Shopify not configured - skipping cart creation');
    throw new Error('Shopify not configured');
  }

  try {
    console.log('Creating Shopify cart with items:', cartItems);
    
    // Validate cart items exist
    if (!cartItems || cartItems.length === 0) {
      throw new Error('No cart items provided');
    }
    
    // Convert cart items with detailed error handling
    let shopifyLines;
    try {
      shopifyLines = convertCartItemsToShopify(cartItems);
      console.log('Converted cart lines:', shopifyLines);
    } catch (conversionError) {
      console.error('Error converting cart items:', conversionError);
      throw new Error(`Cart item conversion failed: ${conversionError.message}`);
    }
    
    const variables = {
      input: {
        lines: shopifyLines
      }
    };
    
    console.log('Cart create variables:', JSON.stringify(variables, null, 2));
    
    const response = await client.request(CREATE_CART_MUTATION, { variables });
    
    console.log('Cart create response:', response);
    
    if (response.errors) {
      console.error('GraphQL errors:', response.errors);
      throw new Error(`GraphQL errors: ${JSON.stringify(response.errors)}`);
    }
    
    const cartData = response.data?.cartCreate;
    
    if (!cartData) {
      throw new Error('No cart data in response');
    }
    
    if (cartData.userErrors && cartData.userErrors.length > 0) {
      console.error('Cart creation errors:', cartData.userErrors);
      throw new Error(`Cart creation failed: ${cartData.userErrors.map(err => err.message).join(', ')}`);
    }
    
    if (!cartData.cart) {
      throw new Error('No cart object in response');
    }
    
    console.log('✅ Cart created successfully:', cartData.cart.id);
    return cartData.cart;
    
  } catch (error) {
    console.error('❌ Error creating cart:', error);
    
    // Enhanced error handling
    let errorMessage = error.message || 'Unknown error occurred';
    
    if (error.networkStatusCode) {
      errorMessage = `Network error (${error.networkStatusCode}): ${errorMessage}`;
    }
    
    if (error.graphQLErrors && error.graphQLErrors.length > 0) {
      const graphQLMessages = error.graphQLErrors.map(err => err.message).join(', ');
      errorMessage = `GraphQL errors: ${graphQLMessages}`;
    }
    
    throw new Error(`Invalid response from Shopify API: ${JSON.stringify({
      headers: error.headers || {},
      errors: {
        networkStatusCode: error.networkStatusCode || null,
        message: errorMessage,
        graphQLErrors: error.graphQLErrors || [],
        response: error.response || {}
      }
    })}`);
  }
};

/**
 * Update cart buyer identity (email, phone, country)
 */
export const updateCartBuyerIdentity = async (cartId, buyerIdentity) => {
  if (!client) {
    console.warn('Shopify not configured - skipping buyer identity update');
    throw new Error('Shopify not configured');
  }

  try {
    console.log('Updating cart buyer identity:', { cartId, buyerIdentity });
    
    const variables = {
      cartId,
      buyerIdentity
    };
    
    const response = await client.request(UPDATE_CART_BUYER_IDENTITY_MUTATION, { variables });
    
    console.log('Buyer identity update response:', response);
    
    if (response.errors) {
      console.error('GraphQL errors:', response.errors);
      throw new Error(`GraphQL errors: ${JSON.stringify(response.errors)}`);
    }
    
    const updateData = response.data?.cartBuyerIdentityUpdate;
    
    if (!updateData) {
      throw new Error('No update data in response');
    }
    
    if (updateData.userErrors && updateData.userErrors.length > 0) {
      console.error('Buyer identity update errors:', updateData.userErrors);
      throw new Error(`Update failed: ${updateData.userErrors.map(err => err.message).join(', ')}`);
    }
    
    console.log('✅ Cart buyer identity updated successfully');
    return updateData.cart;
    
  } catch (error) {
    console.error('❌ Error updating cart buyer identity:', error);
    throw error;
  }
};

/**
 * Get cart by ID
 */
export const getCart = async (cartId) => {
  if (!client) {
    console.warn('Shopify not configured - skipping cart fetch');
    throw new Error('Shopify not configured');
  }

  try {
    console.log('Fetching cart:', cartId);
    
    const variables = { id: cartId };
    const response = await client.request(CART_QUERY, { variables });
    
    console.log('Cart query response:', response);
    
    if (response.errors) {
      console.error('GraphQL errors:', response.errors);
      throw new Error(`GraphQL errors: ${JSON.stringify(response.errors)}`);
    }
    
    const cart = response.data?.cart;
    
    if (!cart) {
      throw new Error('Cart not found');
    }
    
    return cart;
    
  } catch (error) {
    console.error('❌ Error fetching cart:', error);
    throw error;
  }
};

/**
 * Create a draft order with custom pricing via backend API - this allows any price amount
 */
export const createDraftOrderWithCustomPrice = async (orderData) => {
  try {
    console.log('Creating draft order with custom pricing via backend:', orderData);
    
    // Call our backend endpoint instead of direct Admin API
    const response = await fetch(`${process.env.REACT_APP_API_URL}/create-draft-order`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ orderData }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    
    console.log('Backend response:', data);

    if (!data.success) {
      throw new Error(data.error || 'Draft order creation failed');
    }

    if (!data.draftOrder) {
      throw new Error('No draft order in response');
    }

    console.log('✅ Draft order created successfully via backend:', data.draftOrder);
    return data.draftOrder;

  } catch (error) {
    console.error('❌ Error creating draft order via backend:', error);
    throw new Error(`Failed to create custom price draft order: ${error.message}`);
  }
};

/**
 * Get draft order checkout URL
 */
export const getDraftOrderCheckoutUrl = (draftOrder) => {
  // Draft orders use invoiceUrl for payment
  return draftOrder.invoiceUrl;
};

/**
 * Apply discount codes to cart
 */
export const applyDiscountToCart = async (cartId, discountCodes) => {
  if (!client) {
    console.warn('Shopify not configured - skipping discount application');
    throw new Error('Shopify not configured');
  }

  try {
    console.log('Applying discount codes to cart:', { cartId, discountCodes });
    
    const variables = {
      cartId,
      discountCodes
    };
    
    const response = await client.request(CART_DISCOUNT_CODES_UPDATE_MUTATION, { variables });
    
    console.log('Discount application response:', response);
    
    if (response.errors) {
      console.error('GraphQL errors:', response.errors);
      throw new Error(`GraphQL errors: ${JSON.stringify(response.errors)}`);
    }
    
    const updateData = response.data?.cartDiscountCodesUpdate;
    
    if (!updateData) {
      throw new Error('No discount update data in response');
    }
    
    if (updateData.userErrors && updateData.userErrors.length > 0) {
      console.error('Discount application errors:', updateData.userErrors);
      throw new Error(`Discount application failed: ${updateData.userErrors.map(err => err.message).join(', ')}`);
    }
    
    console.log('✅ Discount codes applied successfully');
    return updateData.cart;
    
  } catch (error) {
    console.error('❌ Error applying discount codes:', error);
    throw error;
  }
};

export default client; 